import eventBus from "@common/events/common-event-bus"
import memberEvents from "@/modules/member-management/events/events"
import useAsclepius from "@common/composables/api/asclepius"
import { computed, reactive, ref, unref, watch } from "vue"
import type { ComputedRef, Ref } from "vue"
import { keepPreviousData, useQuery } from "@tanstack/vue-query"
import { QueryApiCacheKeys } from "@common/config/query-api-cache-keys"
import {
  CompanyMember,
  CompanyMemberApiCreateCompanyEmployeeRequest,
  CompanyMemberApiCreateCompanyRecruitRequest,
  CompanyMemberApiCreateCompanyYouthRequest,
  CompanyMemberApiDeactivateCompanyMemberRequest,
  CompanyMemberApiListCompanyMembersRequest,
  CompanyMemberApiPromoteCompanyRecruitToEmployeeRequest,
  CompanyMemberApiUpdateCompanyMemberRequest,
  CompanyMemberTypeEnum,
  Employee,
  EmployeeResponse,
  PatchedUpdateEmployeeRequest,
  Tag
} from "@vitable/asclepius-api-client-ts"
import { useCompanyStore } from "@/store/company-store"

export type EmployeeDetail = Employee
export type PatchEmployeeRequest = PatchedUpdateEmployeeRequest

export const useGetMember = () => {
  const { getMemberApi, callWithResponse, callResult } = useAsclepius()

  const member = ref(null)

  async function getMember(memberId: string) {
    await callWithResponse(() =>
      getMemberApi().retrieveMember({
        memberId
      })
    )
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      const data = newValue.getData()
      member.value = data.member
    }
  })

  return {
    getMember,
    getMemberResult: callResult,
    member
  }
}

export const useUpdateCompanyMember = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()
  const companyMember = ref<Nullable<CompanyMember>>(null)
  const emitChangeEventIn = ref(true)

  async function updateCompanyMember(
    request: CompanyMemberApiUpdateCompanyMemberRequest,
    options: { emitChangeEventIn?: boolean } = {}
  ) {
    emitChangeEventIn.value = options.emitChangeEventIn ?? true
    await callWithResponse(() => getCompanyMemberApi().updateCompanyMember(request))
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      companyMember.value = newValue.getData().company_member
      if (emitChangeEventIn.value) {
        eventBus.$emit(memberEvents.COMPANY_MEMBERS_CHANGED)
      }
    }
  })

  return {
    updateCompanyMember,
    updateMemberResult: callResult
  }
}

export const useGetEmployee = () => {
  const { getEmployeesApi, callWithResponse, callResult } = useAsclepius()
  const employee = ref<Nullable<EmployeeDetail>>(null)

  async function getEmployee(employeeId: string) {
    await callWithResponse<EmployeeResponse>(() => getEmployeesApi().getEmployee({ employeeId }))
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      employee.value = newValue.getData().data
    }
  })

  return {
    getEmployee,
    getEmployeeResult: callResult,
    employee
  }
}

export const usePatchEmployee = () => {
  const { getEmployeesApi, callWithResponse, callResult } = useAsclepius()
  const employee = ref<Nullable<EmployeeDetail>>(null)

  async function patchEmployee(employeeId: string, request: PatchEmployeeRequest) {
    await callWithResponse<EmployeeResponse>(() =>
      getEmployeesApi().updateEmployee({ employeeId, patchedUpdateEmployeeRequest: request })
    )
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      employee.value = newValue.getData().data
    }
  })

  return {
    patchEmployee,
    patchEmployeeResult: callResult,
    employee
  }
}

// TODO: @Deprecated -- DO NOT USE, plz see useListCompanyMembers below - a much simpler version
/**
 * Lists all Company Members within the logged-in user's company.
 */
export const useListCompanyPeople = () => {
  const companyStore = useCompanyStore()

  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()

  const members = reactive({
    currentPage: 1,
    nextPage: 1,
    prevPage: 0,
    totalCount: 0,
    results: []
  })

  type listCompanyMembersParams = {
    email?: Optional<string>
    phone?: Optional<string>
    name?: Optional<string>
    memberId?: Optional<string>
    type?: Optional<CompanyMemberTypeEnum>
    activeIn?: Optional<boolean>
    tags?: Optional<Array<Tag>>
    includeTags?: Optional<boolean>
    limit?: Optional<number>
    missingCriticalEmployeeInfoIn?: Optional<boolean>
  }

  async function listCompanyMembers(
    {
      email,
      phone,
      name,
      memberId,
      type,
      activeIn,
      tags,
      includeTags,
      limit,
      missingCriticalEmployeeInfoIn
    }: listCompanyMembersParams = {
      activeIn: true,
      includeTags: true,
      limit: 20
    }
  ) {
    const companyId = companyStore.requireCompanyId("Choose an employer before loading company members.")

    await callWithResponse(() =>
      getCompanyMemberApi().listCompanyMembers({
        companyId,
        type: type,
        activeIn: activeIn,
        memberId: memberId,
        memberEmail: email,
        memberPhone: phone,
        memberName: name,
        tagsIds: tags ? tags.map((i) => i.id) : undefined,
        includeTagsIn: includeTags,
        missingCriticalEmployeeInfoIn: missingCriticalEmployeeInfoIn,
        page: members.currentPage,
        limit: limit
      })
    )
  }

  type searchCompanyMembersParams = {
    searchInput: string
    tags?: Optional<Array<Tag>>
    includeTagsIn: Optional<boolean>
    type?: Optional<CompanyMemberTypeEnum>
    activeIn: Optional<boolean>
    coveredIn: Optional<boolean>
    missingCriticalEmployeeInfoIn: Optional<boolean>
  }

  async function searchCompanyMembers({
    searchInput,
    tags,
    includeTagsIn,
    type,
    activeIn,
    missingCriticalEmployeeInfoIn
  }: searchCompanyMembersParams) {
    const params: listCompanyMembersParams = {}

    if (type) params.type = type
    if (activeIn !== undefined) params.activeIn = activeIn
    if (includeTagsIn !== undefined) params.includeTags = includeTagsIn
    if (tags) params.tags = tags
    if (searchInput && searchInput.trim() !== "") {
      if (searchInput.includes("@")) {
        params.email = searchInput
      } else if (/\d/.test(searchInput)) {
        params.phone = searchInput
      } else {
        params.name = searchInput
      }
    }
    if (missingCriticalEmployeeInfoIn) params.missingCriticalEmployeeInfoIn = missingCriticalEmployeeInfoIn

    await listCompanyMembers(params)
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      const data = callResult.value.getData()
      members.nextPage = data.next
      members.prevPage = data.previous
      members.totalCount = data.count
      members.results = data.results
    }
  })

  return {
    listCompanyMembers,
    listCompanyMembersResult: callResult,
    searchCompanyMembers,
    members
  }
}

export const useListCompanyMembers = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()

  const companyMembersPaginated = reactive<Paginated<CompanyMember>>({
    currentPage: 1,
    nextPage: "",
    prevPage: "",
    totalCount: 0,
    results: []
  })

  async function listCompanyMembers(request: CompanyMemberApiListCompanyMembersRequest) {
    request = { ...request, page: companyMembersPaginated.currentPage }
    await callWithResponse(() => getCompanyMemberApi().listCompanyMembers(request))
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      companyMembersPaginated.results = newValue.data.results
      // @ts-ignore
      companyMembersPaginated.nextPage = newValue.data.next
      // @ts-ignore
      companyMembersPaginated.prevPage = newValue.data.previous
      // @ts-ignore
      companyMembersPaginated.totalCount = newValue.data.count
    }
  })

  return {
    listCompanyMembers,
    listCompanyMembersResult: callResult,
    companyMembersPaginated
  }
}

export const useListCompanyMembersQuery = (
  request: Ref<CompanyMemberApiListCompanyMembersRequest> | ComputedRef<CompanyMemberApiListCompanyMembersRequest>,
  options?: {
    enabled?: Ref<boolean> | ComputedRef<boolean> | boolean
    refetchInterval?: Ref<number | false> | ComputedRef<number | false> | number | false
  }
) => {
  const { getCompanyMemberApi } = useAsclepius()

  return useQuery({
    queryKey: computed(() => {
      const currentRequest = request.value
      return [QueryApiCacheKeys.COMPANY_MEMBERS, { ...currentRequest }]
    }),
    queryFn: async () => {
      const response = await getCompanyMemberApi().listCompanyMembers(request.value)
      return response.data
    },
    placeholderData: keepPreviousData,
    enabled: computed(() => (options?.enabled == null ? true : unref(options.enabled))),
    refetchInterval: computed(() => (options?.refetchInterval == null ? false : unref(options.refetchInterval)))
  })
}

/**
 * Gets an existing Company Member within the logged-in user's company.
 */
export const useGetCompanyMember = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()

  const companyMember = ref<CompanyMember>()

  async function getCompanyMember(companyMemberId: string, { includeTagsIn } = { includeTagsIn: false }) {
    await callWithResponse(() =>
      getCompanyMemberApi().getCompanyMember({
        companyMemberId: companyMemberId,
        includeTagsIn: includeTagsIn
      })
    )
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      const data = newValue.getData()
      companyMember.value = data.company_member
    }
  })

  return {
    getCompanyMember,
    getCompanyMemberResult: callResult,
    companyMember
  }
}

/**
 * Create a new Company Employee with logged-in user's Company.
 */
export const useCreateCompanyEmployee = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()
  const createdCompanyEmployee = ref(null)

  async function createCompanyEmployee(request: CompanyMemberApiCreateCompanyEmployeeRequest) {
    await callWithResponse(() => getCompanyMemberApi().createCompanyEmployee(request))
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      const data = newValue.getData()
      createdCompanyEmployee.value = data.company_member

      eventBus.$emit(memberEvents.COMPANY_EMPLOYEE_ADDED, data.company_member)
      eventBus.$emit(memberEvents.COMPANY_MEMBERS_CHANGED)
    }
  })

  return {
    createCompanyEmployee,
    createCompanyEmployeeResult: callResult,
    createdCompanyEmployee
  }
}

/**
 * Create a new Company Recruit with logged-in user's Company.
 */
export const useCreateCompanyRecruit = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()
  const createdCompanyRecruit = ref(null)

  async function createCompanyRecruit(request: CompanyMemberApiCreateCompanyRecruitRequest) {
    await callWithResponse(() => getCompanyMemberApi().createCompanyRecruit(request))
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      const data = newValue.getData()
      createdCompanyRecruit.value = data.company_member

      eventBus.$emit(memberEvents.COMPANY_RECRUIT_ADDED, data.company_member)
      eventBus.$emit(memberEvents.COMPANY_MEMBERS_CHANGED)
    }
  })

  return {
    createCompanyRecruit,
    createCompanyRecruitResult: callResult,
    createdCompanyRecruit
  }
}

/**
 * Create a new Company Youth
 */
export const useCreateCompanyYouth = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()
  const createdCompanyYouth = ref(null)

  async function createCompanyYouth(request: CompanyMemberApiCreateCompanyYouthRequest) {
    await callWithResponse(() => getCompanyMemberApi().createCompanyYouth(request))
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      const data = newValue.getData()
      createdCompanyYouth.value = data.company_member

      eventBus.$emit(memberEvents.COMPANY_YOUTH_ADDED, data.company_member)
      eventBus.$emit(memberEvents.COMPANY_MEMBERS_CHANGED)
    }
  })

  return {
    createCompanyYouth,
    createCompanyYouthResult: callResult,
    createdCompanyYouth
  }
}

/**
 * Reactivates a Company Employee for Company.
 */
export const useReactivateCompanyEmployee = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()

  let lastCompanyMemberId: Nullable<string> = null

  async function reactivateCompanyEmployee(companyMemberId: string) {
    lastCompanyMemberId = companyMemberId
    await callWithResponse(() =>
      getCompanyMemberApi().reactivateCompanyMember({
        companyMemberId: companyMemberId
      })
    )
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      eventBus.$emit(memberEvents.COMPANY_EMPLOYEE_REACTIVATED, lastCompanyMemberId)
      eventBus.$emit(memberEvents.COMPANY_MEMBERS_CHANGED)
    }
  })

  return {
    reactivateCompanyEmployee,
    reactivateCompanyEmployeeResult: callResult
  }
}

/**
 * Removes a Company Employee from Company.
 */
export const useDeactivateCompanyEmployee = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()

  let lastRemovedCompanyMemberId: Nullable<string> = null

  async function removeCompanyEmployee(request: CompanyMemberApiDeactivateCompanyMemberRequest) {
    lastRemovedCompanyMemberId = request.companyMemberId
    await callWithResponse(() => getCompanyMemberApi().deactivateCompanyMember(request))
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      if (lastRemovedCompanyMemberId) {
        eventBus.$emit(memberEvents.COMPANY_EMPLOYEE_DEACTIVATED, lastRemovedCompanyMemberId)
      }
      eventBus.$emit(memberEvents.COMPANY_MEMBERS_CHANGED)
    }
  })

  return {
    removeCompanyEmployee,
    removeCompanyEmployeeResult: callResult
  }
}

/**
 * Promotes a Company Recruit to a Company Employee.
 */
export const usePromoteCompanyRecruitToEmployee = () => {
  const { getCompanyMemberApi, callWithResponse, callResult } = useAsclepius()

  const promotedCompanyMember = ref<CompanyMember>()

  async function promoteCompanyRecruitToEmployee(request: CompanyMemberApiPromoteCompanyRecruitToEmployeeRequest) {
    await callWithResponse(() => getCompanyMemberApi().promoteCompanyRecruitToEmployee(request))
  }

  watch(callResult, (newValue) => {
    // @ts-ignore
    if (newValue.isSuccess()) {
      // @ts-ignore
      const data = newValue.getData()
      promotedCompanyMember.value = data.company_member

      eventBus.$emit(memberEvents.COMPANY_EMPLOYEE_ADDED, data.company_member)
      eventBus.$emit(memberEvents.COMPANY_MEMBERS_CHANGED)
    }
  })

  return {
    promoteCompanyRecruitToEmployee,
    promoteCompanyRecruitToEmployeeResult: callResult,
    promotedCompanyMember
  }
}
